import { useCallback } from 'react';
import {
  Box,
  Button,
  ScrollPane,
  SegmentedControl,
} from '@nimbus-ds/components';
import { ChevronLeftIcon, ChevronRightIcon } from '@nimbus-ds/icons';
import { useResponsive } from 'commons/components';
import { defaultSavedSearches } from 'domains/Orders/components/SavedSearches/defaultSavedSearches';
import {
  defaultFilters,
  FiltersParamsType,
  FiltersType,
} from 'domains/Orders/Orders/ordersService/constants';
import { trackingSearchBySavedSearches } from 'domains/Orders/tracking';
import { SavedSearch } from './SavedSearch/SavedSearch';
import { SavedSearchInterface } from './types';
import useSavedSearches from './useSavedSearches';
import useSelectedSavedSearch from './useSelectedSavedSearch';
import './SavedSearches.scss';

interface SavedSearchesProperties {
  appliedFilter: FiltersParamsType;
  onSavedSearchClick: (newFilters: FiltersParamsType) => void;
  visible: boolean;
}

export function SavedSearches({
  appliedFilter,
  onSavedSearchClick,
  visible,
}: Readonly<SavedSearchesProperties>) {
  const savedSearches: SavedSearchInterface[] = defaultSavedSearches;

  const { isMobile } = useResponsive();
  const { selectedSavedSearch, setSelectedSavedSearch } =
    useSelectedSavedSearch(appliedFilter);
  const { savedSearchesCount } = useSavedSearches();

  const onClick = useCallback(
    (savedSearch) => {
      // Saved search is already selected, so it should be deselected
      if (selectedSavedSearch === savedSearch.code) {
        onSavedSearchClick(defaultFilters);
        setSelectedSavedSearch('');
        trackingSearchBySavedSearches(savedSearch.code, false);
      } else {
        const transformedFilters: FiltersType = {
          ...defaultFilters,
          ...savedSearch.filters,
        };
        onSavedSearchClick(transformedFilters);
        setSelectedSavedSearch(savedSearch.code);
        trackingSearchBySavedSearches(savedSearch.code, true);
      }
    },
    [onSavedSearchClick, selectedSavedSearch, setSelectedSavedSearch],
  );

  return (
    <Box display={visible ? 'block' : 'none'}>
      <div className="stratus--orders-saved-searches-scroll-pane">
        <ScrollPane
          display="grid"
          contentContainerProps={{
            flexShrink: '0',
          }}
          direction="horizontal"
          scrollPaneArrowEnd={
            <ScrollPane.ArrowHorizontalEnd>
              <div className="stratus--orders-saved-searches-arrow">
                <Button>
                  <ChevronRightIcon size="small" />
                </Button>
              </div>
            </ScrollPane.ArrowHorizontalEnd>
          }
          scrollPaneArrowStart={
            <ScrollPane.ArrowHorizontalStart>
              <div className="stratus--orders-saved-searches-arrow">
                <Button>
                  <ChevronLeftIcon size="small" />
                </Button>
              </div>
            </ScrollPane.ArrowHorizontalStart>
          }
          showGradients={isMobile}
          scrollToItemOnClick
          showArrows={!isMobile}
          showScrollbar={false}
        >
          <SegmentedControl selectedSegments={[selectedSavedSearch]}>
            {savedSearches.map((savedSearch) => (
              <SavedSearch
                key={savedSearch.code}
                isSelected={savedSearch.code === selectedSavedSearch}
                savedSearch={savedSearch}
                count={savedSearchesCount?.[savedSearch.code]?.count}
                onSavedSearchClick={onClick}
              />
            ))}
          </SegmentedControl>
        </ScrollPane>
      </div>
    </Box>
  );
}
